import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SpinnerGap } from "@phosphor-icons/react";
import { Logo, LogoMark } from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-primary-foreground relative overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1767424412548-1a1ac7f4b9bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxzdG9jayUyMG1hcmtldCUyMHRyYWRpbmclMjBjYW5kbGVzdGljayUyMGNoYXJ0JTIwc2NyZWVufGVufDB8fHx8MTc4ODI0MDMzOHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-45 kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/80 to-indigo-900/70" />
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <LogoMark size={30} />
          <span className="font-heading font-bold text-xl text-white">Inves<span className="text-indigo-400">y</span></span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-heading font-bold text-4xl tracking-tight leading-tight">
            Smarter money decisions, one login away.
          </h2>
          <p className="mt-4 text-primary-foreground/80 max-w-sm">
            Pick up right where you left off with your AI financial copilot.
          </p>
        </div>
        <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-accent/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Logo size={30} />
          </div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground mt-1 mb-8">Log in to your copilot.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 p-4 h-12 rounded-lg"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 p-4 h-12 rounded-lg"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 gap-2"
            >
              {loading && <SpinnerGap size={18} className="animate-spin" />}
              Log in
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            New to Invesy?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
